import { useEffect, useState } from 'react';

/** Una opción tap-or-speak de la red A0 (plan 33 §1.1). El `en` se envía al tocar. */
export interface A0Suggestion {
  en: string;
  es: string;
}

/** El paso de sugerencias del objetivo pendiente (lo emite el server por el WS). */
export interface A0SuggestionStep {
  objectiveId: string;
  /** Meta en español del paso ("Saludá al coach"). */
  goalEs: string;
  /** Andamiaje vigente — capa default de la red §1.1 (full/medium/light). */
  stage: 'full' | 'medium' | 'light';
  suggestions: readonly A0Suggestion[];
}

export interface UseA0SuggestionsReturn {
  /**
   * Paso de sugerencias VIGENTE (objetivo pendiente), o `null` cuando: no hay
   * misión, el objetivo del paso ya se cumplió (se retira hasta que llegue el
   * próximo), o se cumplieron todos. El front renderiza los chips sólo con esto.
   */
  step: A0SuggestionStep | null;
}

/**
 * Red A0 (plan 33 §1.1) — sugerencias tap-or-speak del objetivo pendiente. El
 * server las emite por el WS (`a0.suggestions`): una al arrancar (objetivo 1) y
 * de nuevo en cada avance. Este hook las consume del window event que el
 * voice-client redispara (igual patrón que `useObjectiveTrackers`).
 *
 * GOTCHA (memoria del plan 32): montá este hook SIEMPRE — nunca condicional por
 * `mode==='a0'` (es el patrón que ya explotó con "Rendered more hooks"). El gate
 * vive en el `useEffect` (no se suscribe sin misión) y en el RENDER (la shell
 * sólo dibuja los chips si hay misión A0). El orden de hooks no cambia con el modo.
 *
 * Cuando el objetivo del paso vigente se cumple (`objective.progress`), retiramos
 * el paso (chips stale) hasta que llegue el del SIGUIENTE objetivo. Si era el
 * último, no llega ninguno más → la red se retira sola (misión completa).
 */
export function useA0Suggestions(args: { hasMission: boolean }): UseA0SuggestionsReturn {
  const { hasMission } = args;
  const [step, setStep] = useState<A0SuggestionStep | null>(null);

  useEffect(() => {
    if (!hasMission) return;
    const onSuggestions = (ev: Event): void => {
      const detail: unknown = (ev as CustomEvent<unknown>).detail;
      if (typeof detail !== 'object' || detail === null) return;
      const d = detail as Record<string, unknown>;
      const { objectiveId, goalEs, stage, suggestions } = d;
      if (
        typeof objectiveId !== 'string' ||
        typeof goalEs !== 'string' ||
        (stage !== 'full' && stage !== 'medium' && stage !== 'light') ||
        !Array.isArray(suggestions)
      ) {
        return;
      }
      const clean: A0Suggestion[] = [];
      for (const item of suggestions as unknown[]) {
        if (typeof item !== 'object' || item === null) continue;
        const s = item as Record<string, unknown>;
        if (typeof s.en === 'string' && typeof s.es === 'string' && s.en.length > 0) {
          clean.push({ en: s.en, es: s.es });
        }
      }
      if (clean.length === 0) return;
      setStep({ objectiveId, goalEs, stage, suggestions: clean });
    };
    // Cuando el objetivo del paso vigente se cumple, sus chips quedan stale →
    // los retiramos hasta que llegue el del próximo objetivo (o nunca, si era el
    // último → la red se retira sola). Optimista, como el tracker de objetivos.
    const onProgress = (ev: Event): void => {
      const detail = (ev as CustomEvent).detail as
        | { objectiveId?: unknown; met?: unknown }
        | undefined;
      if (!detail || typeof detail.objectiveId !== 'string' || detail.met !== true) return;
      const metId = detail.objectiveId;
      setStep((prev) => (prev && prev.objectiveId === metId ? null : prev));
    };
    window.addEventListener('orborix:a0.suggestions', onSuggestions);
    window.addEventListener('orborix:objective.progress', onProgress);
    return () => {
      window.removeEventListener('orborix:a0.suggestions', onSuggestions);
      window.removeEventListener('orborix:objective.progress', onProgress);
    };
  }, [hasMission]);

  return { step };
}
